import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  applyForMembership,
  getMyMembershipStatus,
  initializeMembershipPayment,
  downloadWalletCard,
  downloadAppleWalletPass,
  getMembershipPricing,
} from '../services/membershipService';
import COUNTRIES, { findCountryByName } from '../utils/countries';
import './Membership.css';

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
const normalizePhone = (p) => {
  const digits = (p || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  // Egyptian local format (e.g. 01226294984) -> international (201226294984)
  if (digits.startsWith('0')) return '20' + digits.slice(1);
  return digits;
};
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');
const MIN_AGE_YEARS = 23;
const maxBirthdate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_AGE_YEARS);
  return d.toISOString().slice(0, 10);
};
const isValidBirthdate = (v) => !v || v <= maxBirthdate();
const SOCIAL_LINK_REGEX = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s]*)?$/;
const isValidSocialLink = (v) => !v || SOCIAL_LINK_REGEX.test(v.trim());
const pickNonEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

// Admin rich-text (Quill) can save non-breaking spaces and inline white-space:
// nowrap, both of which make a line unbreakable so it runs off the box instead
// of wrapping. Normalize them to regular, breakable whitespace before render so
// the text wraps by whole words and stays inside the card.
const sanitizeInfoHtml = (html) =>
  (html || '')
    .replace(/&nbsp;|&#160;|&#xA0;| /gi, ' ')
    .replace(/[   ⁠﻿​]/g, ' ')
    .replace(/[   ⁠​﻿]/g, ' ')
    .replace(/white-space\s*:\s*nowrap/gi, 'white-space:normal')
    .replace(/word-break\s*:\s*break-all/gi, 'word-break:normal');

// ---------- field validators ----------
// Letters (any language), spaces, hyphens, apostrophes and dots only.
const NAME_REGEX = /^[\p{L}][\p{L} .'-]*$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const nameError = (v, label = 'Full name') => {
  const val = (v || '').trim();
  if (!val) return `${label} is required.`;
  if (val.length < 3) return `${label} must be at least 3 characters.`;
  if (val.length > 60) return `${label} must be 60 characters or fewer.`;
  if (!NAME_REGEX.test(val)) return `${label} can only contain letters, spaces, hyphens and apostrophes.`;
  return '';
};
const emailError = (v) => {
  const val = (v || '').trim();
  if (!val) return 'Email is required.';
  if (!EMAIL_REGEX.test(val)) return 'Please enter a valid email address (e.g. name@example.com).';
  return '';
};
const phoneError = (v, label = 'Phone number') => {
  const digits = (v || '').replace(/\D/g, '');
  if (!digits) return `${label} is required.`;
  if (digits.length < 8 || digits.length > 15) return `Please enter a valid ${label.toLowerCase()}.`;
  return '';
};
const socialError = (v, label = 'Social media link', example = 'https://instagram.com/yourhandle') => {
  const val = (v || '').trim();
  if (!val) return `${label} is required.`;
  if (!isValidSocialLink(val)) return `Please enter a valid link (e.g. ${example}).`;
  return '';
};

// Photo upload constraints — must match backend membership-photo filter / limits.
const ALLOWED_PHOTO_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB
const photoFileError = (file, label = 'Photo') => {
  if (!file) return `${label} is required.`;
  if (!ALLOWED_PHOTO_MIMES.includes(file.type)) {
    return `${label} must be a JPEG, PNG or WebP image.`;
  }
  if (file.size > MAX_PHOTO_BYTES) return `${label} must be 5 MB or smaller.`;
  return '';
};

// Order used to scroll to the first field with an error.
const FIELD_ORDER = [
  'name', 'email', 'phone', 'socialMediaLink', 'birthdate', 'photo',
  'coupleName', 'couplePhone', 'coupleEmail', 'coupleSocialMediaLink', 'couplePhoto',
];

const Membership = () => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [pricing, setPricing] = useState(null);
  // Drives the Phone Number input's country flag/code. Defaults to UAE and is
  // updated when the user picks a Country of Residence below.
  const [phoneCountry, setPhoneCountry] = useState('ae');

  const [formData, setFormData] = useState({
    type: 1, // 1 = SINGLE, 2 = COUPLE
    authCapture: true, // Always true - payment before admin approval
    name: '',
    phone: '',
    email: '',
    socialMediaLink: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressMoreInfo: '',
    residenceCountry: '',
    birthdate: '',
    gender: 1, // 1 = MALE, 2 = FEMALE
    // Couple fields (only if type === 2)
    coupleName: '',
    couplePhone: '',
    coupleEmail: '',
    coupleSocialMediaLink: '',
  });

  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [couplePhotoFile, setCouplePhotoFile] = useState(null);
  const [couplePhotoPreview, setCouplePhotoPreview] = useState('');
  const [showAllBenefits, setShowAllBenefits] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchMembershipStatus();
    fetchPricing();
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const fromUser = pickNonEmpty({
      name: fullName(user),
      email: user?.email,
      phone: normalizePhone(user?.phoneNumber),
    });

    const m = membershipStatus?.membership;
    const fromMembership = m
      ? pickNonEmpty({
          type: m.type != null ? Number(m.type) : undefined,
          name: m.name,
          email: m.email,
          phone: normalizePhone(m.phone),
          socialMediaLink: m.socialMediaLink,
          birthdate: toDateInput(m.birthdate),
          gender: m.gender != null ? Number(m.gender) : undefined,
          addressStreet: m.addressStreet,
          addressNeighborhood: m.addressNeighborhood,
          addressMoreInfo: m.addressMoreInfo,
          residenceCountry: m.residenceCountry,
          coupleName: m.coupleName,
          couplePhone: normalizePhone(m.couplePhone),
          coupleEmail: m.coupleEmail,
          coupleSocialMediaLink: m.coupleSocialMediaLink,
        })
      : {};

    setFormData((prev) => ({ ...prev, ...fromUser, ...fromMembership }));
  }, [user, membershipStatus]);

  const fetchMembershipStatus = async () => {
    try {
      setLoading(true);
      const data = await getMyMembershipStatus();
      setMembershipStatus(data);
      // Show form if user can apply OR if there's no membership at all
      setShowForm(data.canApply === true || (data.status === 'none' && !data.hasMembership));
    } catch (err) {
      console.error('Error fetching membership status:', err);
      setError('Failed to load membership status');
      // On error, show the form anyway so user can try to apply
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const data = await getMembershipPricing();
      setPricing(data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
      // Use default pricing if fetch fails
      setPricing({
        single: { priceUsd: 150, duration: 12 },
        couple: { priceUsd: 250, duration: 12 },
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear this field's error as soon as the user starts correcting it.
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handlePhoneChange = (value, field) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  // Selecting a Country of Residence also sets the Phone Number field's country:
  // we update the phone input's country (flag) and reset the phone to that
  // country's dial code so the prefix matches the selection.
  const handleResidenceCountryChange = (e) => {
    const name = e.target.value;
    const country = findCountryByName(name);
    setFormData((prev) => ({
      ...prev,
      residenceCountry: name,
      ...(country ? { phone: country.dialCode } : {}),
    }));
    if (country) setPhoneCountry(country.iso2);
    setFieldErrors((prev) => (prev.phone ? { ...prev, phone: '' } : prev));
  };

  const handlePhotoChange = (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const label = field === 'couplePhoto' ? "Partner's photo" : 'Photo';
    const err = photoFileError(file, label);
    if (err) {
      setFieldErrors((prev) => ({ ...prev, [field]: err }));
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (field === 'couplePhoto') {
        setCouplePhotoFile(file);
        setCouplePhotoPreview(reader.result);
      } else {
        setPhotoFile(file);
        setPhotoPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const handleRemovePhoto = (field) => {
    if (field === 'couplePhoto') {
      setCouplePhotoFile(null);
      setCouplePhotoPreview('');
    } else {
      setPhotoFile(null);
      setPhotoPreview('');
    }
  };

  // Validate every required field and return a { fieldName: message } map.
  const validateForm = () => {
    const errs = {
      name: nameError(formData.name),
      email: emailError(formData.email),
      phone: phoneError(formData.phone),
      socialMediaLink: socialError(formData.socialMediaLink),
      birthdate:
        formData.birthdate && !isValidBirthdate(formData.birthdate)
          ? `You must be at least ${MIN_AGE_YEARS} years old.`
          : '',
      photo: photoFileError(photoFile),
    };
    if (formData.type === 2) {
      errs.coupleName = nameError(formData.coupleName, "Partner's full name");
      errs.couplePhone = phoneError(formData.couplePhone, "Partner's phone");
      errs.coupleEmail = emailError(formData.coupleEmail);
      errs.coupleSocialMediaLink = socialError(
        formData.coupleSocialMediaLink,
        "Partner's social media link",
        'https://instagram.com/partnerhandle',
      );
      errs.couplePhoto = photoFileError(couplePhotoFile, "Partner's photo");
    }
    return Object.fromEntries(Object.entries(errs).filter(([, v]) => v));
  };

  // Validate a single field on blur so users get feedback as they go.
  const validateSingle = (field) => {
    const errs = validateForm();
    setFieldErrors((prev) => ({ ...prev, [field]: errs[field] || '' }));
  };

  // Scroll to (and focus) the first field that has an error.
  const scrollToFirstError = (errs) => {
    const firstKey = FIELD_ORDER.find((k) => errs[k]);
    if (!firstKey) return;
    // On mobile, tapping submit dismisses the on-screen keyboard, and the
    // resulting viewport reflow cancels a scroll fired in the same tick. Blur the
    // active field, then defer the scroll so it runs after the reflow settles
    // (the error messages have also rendered by then, so positions are accurate).
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    window.setTimeout(() => {
      const el = document.getElementById(`field-${firstKey}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = el.querySelector('input, select, textarea');
      if (input) window.setTimeout(() => input.focus({ preventScroll: true }), 350);
    }, 250);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Field-level validation
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      scrollToFirstError(errs);
      return;
    }
    setFieldErrors({});

    try {
      setSubmitting(true);

      // Prepare data by removing empty optional fields
      const submitData = { ...formData };

      // Remove empty optional fields to avoid validation errors
      if (!submitData.birthdate) delete submitData.birthdate;
      if (!submitData.addressStreet) delete submitData.addressStreet;
      if (!submitData.addressNeighborhood) delete submitData.addressNeighborhood;
      if (!submitData.addressMoreInfo) delete submitData.addressMoreInfo;
      if (!submitData.residenceCountry) delete submitData.residenceCountry;
      // Drop couple-only fields for single memberships so the multipart body
      // doesn't carry stale partner info if the user switched type mid-flow.
      if (submitData.type !== 2) {
        delete submitData.coupleName;
        delete submitData.couplePhone;
        delete submitData.coupleEmail;
        delete submitData.coupleSocialMediaLink;
      }

      const fd = new FormData();
      Object.entries(submitData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      fd.append('photo', photoFile);
      if (formData.type === 2 && couplePhotoFile) {
        fd.append('couplePhoto', couplePhotoFile);
      }

      console.log('Submitting membership application...');
      const response = await applyForMembership(fd);
      console.log('Application response:', response);

      // Always redirect to payment (auth/capture flow is mandatory)
      if (response.paymentUrl) {
        console.log('Redirecting to payment URL:', response.paymentUrl);
        window.location.href = response.paymentUrl;
        return;
      }

      // If no payment URL (shouldn't happen), show error
      setError('Payment initialization failed. Please try again or contact support.');
    } catch (err) {
      console.error('Membership application error:', err);
      console.error('Error response:', err.response);

      // Handle different error formats
      let errorMessage = 'Failed to submit application';

      if (err.response?.data) {
        const errorData = err.response.data;

        // If message is an array of validation errors
        if (Array.isArray(errorData.message)) {
          errorMessage = (
            <ul className="mb-0">
              {errorData.message.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          );
        }
        // If message is a string
        else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // If error is a string
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    try {
      setSubmitting(true);
      const response = await initializeMembershipPayment(membershipStatus.membership.id);
      window.location.href = response.paymentUrl;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initialize payment');
      setSubmitting(false);
    }
  };

  const handleDownloadWalletCard = async (membershipId) => {
    try {
      const response = await downloadWalletCard(membershipId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `membership-card.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading wallet card:', err);
      setError('Failed to download wallet card. Please try again.');
    }
  };

  const handleDownloadAppleWallet = async (membershipId) => {
    try {
      const response = await downloadAppleWalletPass(membershipId);
      const blob = new Blob([response.data], { type: 'application/vnd.apple.pkpass' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `membership.pkpass`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading Apple Wallet pass:', err);
      if (err.response?.status === 500) {
        setError('Apple Wallet is not configured. Please contact support.');
      } else {
        setError('Failed to add to Apple Wallet. Please try again.');
      }
    }
  };

  const renderMembershipCard = () => {
    if (!membershipStatus || !membershipStatus.membership) return null;

    const { membership } = membershipStatus;
    const isPendingCapture = membershipStatus.status === 'pending_capture';
    const displayStatusName = isPendingCapture ? 'Pending Confirmation' : membership.statusName;
    const displayStatusClass = isPendingCapture ? 'status-pending-capture' : `status-${membership.status}`;

    return (
      <div className="membership-card">
        <div className="membership-header">
          <div>
            <h2>{membership.typeName} Membership</h2>
            <span className={`status-badge ${displayStatusClass}`}>
              {displayStatusName}
            </span>
          </div>
          {membership.code && (
            <div className="membership-code">
              <div className="code-label">Member Code</div>
              <div className="code-value">{membership.code}</div>
            </div>
          )}
        </div>

        <div className="membership-body">
          <div className="member-info">
            <div className="member-info-heading">
              <h3>
                <i className="bi bi-person me-2"></i>
                Member
              </h3>
              {membership.photo && (
                <img
                  src={membership.photo}
                  alt={membership.name}
                  className="member-info-photo"
                />
              )}
            </div>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{membership.name}</span>
            </div>
          </div>

          {String(membership.type) === '2' && (
            <div className="member-info partner-info">
              <div className="member-info-heading">
                <h3>
                  <i className="bi bi-people me-2"></i>
                  Partner
                </h3>
                {membership.couplePhoto && (
                  <img
                    src={membership.couplePhoto}
                    alt={membership.coupleName || 'Partner'}
                    className="member-info-photo"
                  />
                )}
              </div>
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{membership.coupleName || '—'}</span>
              </div>
            </div>
          )}

          <div className="membership-dates">
            <div className="date-info">
              <span className="label">Expires:</span>
              <span className="value">End of Summer 2026</span>
            </div>
          </div>

          {membership.qrCode && (
            <div className="membership-qr-section">
              <div className="qr-label">Scan QR Code</div>
              <img src={membership.qrCode} alt="Membership QR Code" className="qr-code-image-large" />
              <p className="qr-description">Scan this code to verify your membership</p>

              {/* Wallet Actions */}
              {/* <div className="wallet-actions">
                <button
                  className="btn-wallet btn-apple-wallet"
                  onClick={() => handleDownloadAppleWallet(membership.id)}
                  title="Add to Apple Wallet"
                >
                  <i className="bi bi-apple"></i>
                  <span>Add to Apple Wallet</span>
                </button>
              </div> */}
            </div>
          )}
        </div>

        <div className="membership-footer">
          <div className="benefits">
            <h3>Your Benefits</h3>
            <ul>
              <li><i className="bi bi-check-circle-fill"></i> Priority reservations</li>
              <li><i className="bi bi-check-circle-fill"></i> Exclusive member shows</li>
              <li><i className="bi bi-check-circle-fill"></i> Special discounts</li>
              <li><i className="bi bi-check-circle-fill"></i> Personalized experiences</li>
              {showAllBenefits && (
                <>
                  <li><i className="bi bi-check-circle-fill"></i> Free entrance to all the parties throughout the summer</li>
                  <li><i className="bi bi-check-circle-fill"></i> 10 free bouchons to use anytime during the summer</li>
                  <li><i className="bi bi-check-circle-fill"></i> Daily access to the members-only beach area</li>
                  <li><i className="bi bi-check-circle-fill"></i> Minimum-spending does not apply to members</li>
                  <li><i className="bi bi-check-circle-fill"></i> Dedicated VIP phone number for reservations</li>
                  <li><i className="bi bi-check-circle-fill"></i> Skip the line! VIP private entrance into KIKI'S</li>
                </>
              )}
            </ul>
            <button
              type="button"
              className="benefits-toggle"
              onClick={() => setShowAllBenefits((v) => !v)}
            >
              {showAllBenefits ? 'Show less' : 'and more…'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStatusMessage = () => {
    if (!membershipStatus) return null;

    if (membershipStatus.status === 'pending') {
      return (
        <div className="alert alert-info">
          <i className="bi bi-clock-history me-2"></i>
          <strong>Application Under Review</strong>
          <p>Your membership application is being reviewed by our team. You will receive an email notification once it has been processed.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'approved') {
      return (
        <div className="alert alert-success">
          <i className="bi bi-check-circle me-2"></i>
          <strong>Application Approved!</strong>
          <p>Your membership has been approved. Please complete your payment to activate your membership.</p>
          <button className="btn btn-primary mt-3" onClick={handlePay} disabled={submitting}>
            {submitting ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'pending_payment') {
      return (
        <div className="alert alert-warning">
          <i className="bi bi-credit-card me-2"></i>
          <strong>Payment Pending</strong>
          <p>Your payment is being processed. Your membership will be activated once approved.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'pending_capture') {
      return (
        <div className="alert alert-warning">
          <i className="bi bi-hourglass-split me-2"></i>
          <strong>Payment Authorized — Awaiting Confirmation</strong>
          <p>Your payment was authorized successfully. Our team will confirm your membership shortly — you will receive an email once it is active.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'rejected') {
      return (
        <div className="alert alert-danger">
          <i className="bi bi-x-circle me-2"></i>
          <strong>Application Not Approved</strong>
          <p>Unfortunately, your membership application was not approved at this time. You can apply again with updated information.</p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'expired') {
      return (
        <div className="alert alert-secondary">
          <i className="bi bi-calendar-x me-2"></i>
          <strong>Membership Expired</strong>
          <p>Your membership has expired. You can apply for a new membership below.</p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    return null;
  };

  const renderTypeSelector = () => (
    <div className="section membership-type-selector">
      <h3>Membership Type</h3>
      <div className="membership-types">
        <div className="type-card">
          <input
            type="radio"
            id="single"
            name="type"
            value="1"
            checked={formData.type === 1}
            onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
          />
          <label htmlFor="single">
            <div className="type-icon"><i className="bi bi-person"></i></div>
            <div className="type-name">Single</div>
            <div className="type-price">
              ${pricing?.single?.priceUsd || 150} USD / {pricing?.single?.duration || 12} months
            </div>
          </label>
        </div>

        <div className="type-card">
          <input
            type="radio"
            id="couple"
            name="type"
            value="2"
            checked={formData.type === 2}
            onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
          />
          <label htmlFor="couple">
            <div className="type-icon"><i className="bi bi-people"></i></div>
            <div className="type-name">Couple</div>
            <div className="type-price">
              ${pricing?.couple?.priceUsd || 250} USD / {pricing?.couple?.duration || 12} months
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderApplicationForm = () => {
    return (
      <form onSubmit={handleSubmit} className="membership-form" noValidate>
        <h2>Apply for Membership</h2>

        {error && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Please fix the following errors:</strong>
            <div className="mt-2">
              {error}
            </div>
          </div>
        )}

        <div className="section">
          <h3>Personal Information</h3>
          <div className="row">
            <div className="col-md-6 mb-3" id="field-name">
              <label>Full Name *</label>
              <input
                type="text"
                className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                required
              />
              {fieldErrors.name && <div className="field-error">{fieldErrors.name}</div>}
            </div>
            <div className="col-md-6 mb-3" id="field-email">
              <label>Email *</label>
              <input
                type="email"
                className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                required
              />
              {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
            </div>
            <div className={`col-md-6 mb-3 ${fieldErrors.phone ? 'has-error' : ''}`} id="field-phone">
              <label>Phone Number *</label>
              <PhoneInput
                country={phoneCountry}
                value={formData.phone}
                onChange={(value) => handlePhoneChange(value, 'phone')}
                onBlur={() => validateSingle('phone')}
                inputClass="form-control"
              />
              {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
            </div>
            <div className="col-md-6 mb-3" id="field-socialMediaLink">
              <label>Social Media Link *</label>
              <input
                type="url"
                className={`form-control ${fieldErrors.socialMediaLink ? 'is-invalid' : ''}`}
                name="socialMediaLink"
                value={formData.socialMediaLink}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                placeholder="https://instagram.com/yourhandle"
                required
              />
              {fieldErrors.socialMediaLink && (
                <div className="field-error">{fieldErrors.socialMediaLink}</div>
              )}
            </div>
            <div className="col-md-6 mb-3" id="field-birthdate">
              <label>Birthdate</label>
              <input
                type="date"
                className={`form-control ${fieldErrors.birthdate ? 'is-invalid' : ''}`}
                name="birthdate"
                value={formData.birthdate}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                max={maxBirthdate()}
              />
              {fieldErrors.birthdate && <div className="field-error">{fieldErrors.birthdate}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label>Gender</label>
              <select className="form-control" name="gender" value={formData.gender} onChange={handleChange}>
                <option value="1">Male</option>
                <option value="2">Female</option>
              </select>
            </div>
            <div className="col-12 mb-3" id="field-photo">
              <label>Your Photo *</label>
              <input
                type="file"
                className={`form-control ${fieldErrors.photo ? 'is-invalid' : ''}`}
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handlePhotoChange(e, 'photo')}
              />
              <small className="form-text text-muted">JPEG, PNG or WebP. Max 5 MB.</small>
              {photoPreview && (
                <div className="mt-2 d-flex align-items-center gap-3">
                  <img
                    src={photoPreview}
                    alt="Your photo preview"
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleRemovePhoto('photo')}
                  >
                    Remove
                  </button>
                </div>
              )}
              {fieldErrors.photo && <div className="field-error">{fieldErrors.photo}</div>}
            </div>
          </div>
        </div>

        {formData.type === 2 && (
          <div className="section">
            <h3>Partner Information</h3>
            <div className="row">
              <div className="col-md-6 mb-3" id="field-coupleName">
                <label>Partner's Full Name *</label>
                <input
                  type="text"
                  className={`form-control ${fieldErrors.coupleName ? 'is-invalid' : ''}`}
                  name="coupleName"
                  value={formData.coupleName}
                  onChange={handleChange}
                  onBlur={(e) => validateSingle(e.target.name)}
                  required={formData.type === 2}
                />
                {fieldErrors.coupleName && <div className="field-error">{fieldErrors.coupleName}</div>}
              </div>
              <div className={`col-md-6 mb-3 ${fieldErrors.couplePhone ? 'has-error' : ''}`} id="field-couplePhone">
                <label>Partner's Phone *</label>
                <PhoneInput
                  country={'ae'}
                  value={formData.couplePhone}
                  onChange={(value) => handlePhoneChange(value, 'couplePhone')}
                  onBlur={() => validateSingle('couplePhone')}
                  inputClass="form-control"
                />
                {fieldErrors.couplePhone && <div className="field-error">{fieldErrors.couplePhone}</div>}
              </div>
              <div className="col-md-6 mb-3" id="field-coupleEmail">
                <label>Partner's Email *</label>
                <input
                  type="email"
                  className={`form-control ${fieldErrors.coupleEmail ? 'is-invalid' : ''}`}
                  name="coupleEmail"
                  value={formData.coupleEmail}
                  onChange={handleChange}
                  onBlur={(e) => validateSingle(e.target.name)}
                  required={formData.type === 2}
                />
                {fieldErrors.coupleEmail && <div className="field-error">{fieldErrors.coupleEmail}</div>}
              </div>
              <div className="col-md-6 mb-3" id="field-coupleSocialMediaLink">
                <label>Partner's Social Media *</label>
                <input
                  type="url"
                  className={`form-control ${fieldErrors.coupleSocialMediaLink ? 'is-invalid' : ''}`}
                  name="coupleSocialMediaLink"
                  value={formData.coupleSocialMediaLink}
                  onChange={handleChange}
                  onBlur={(e) => validateSingle(e.target.name)}
                  placeholder="https://instagram.com/partnerhandle"
                  required={formData.type === 2}
                />
                {fieldErrors.coupleSocialMediaLink && (
                  <div className="field-error">{fieldErrors.coupleSocialMediaLink}</div>
                )}
              </div>
              <div className="col-12 mb-3" id="field-couplePhoto">
                <label>Partner's Photo *</label>
                <input
                  type="file"
                  className={`form-control ${fieldErrors.couplePhoto ? 'is-invalid' : ''}`}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handlePhotoChange(e, 'couplePhoto')}
                />
                <small className="form-text text-muted">JPEG, PNG or WebP. Max 5 MB.</small>
                {couplePhotoPreview && (
                  <div className="mt-2 d-flex align-items-center gap-3">
                    <img
                      src={couplePhotoPreview}
                      alt="Partner's photo preview"
                      style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleRemovePhoto('couplePhoto')}
                    >
                      Remove
                    </button>
                  </div>
                )}
                {fieldErrors.couplePhoto && (
                  <div className="field-error">{fieldErrors.couplePhoto}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h3>Address Information</h3>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Street Address</label>
              <input
                type="text"
                className="form-control"
                name="addressStreet"
                value={formData.addressStreet}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Neighborhood</label>
              <input
                type="text"
                className="form-control"
                name="addressNeighborhood"
                value={formData.addressNeighborhood}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Country of Residence</label>
              <select
                className="form-control"
                name="residenceCountry"
                value={formData.residenceCountry}
                onChange={handleResidenceCountryChange}
              >
                <option value="">Select country...</option>
                {COUNTRIES.map((country) => (
                  <option key={country.iso2} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label>Additional Info</label>
              <input
                type="text"
                className="form-control"
                name="addressMoreInfo"
                value={formData.addressMoreInfo}
                onChange={handleChange}
                placeholder="Apartment, floor, etc."
              />
            </div>
          </div>
        </div>

        <div className="section">
          <h3>What Happens Next</h3>
          <ol className="payment-steps">
            <li className="payment-step is-current">
              <span className="payment-step-num">1</span>
              <div className="payment-step-body">
                <div className="payment-step-title">Submit your application</div>
                <div className="payment-step-desc">Fill in your details and continue to payment.</div>
              </div>
            </li>
            <li className="payment-step">
              <span className="payment-step-num">2</span>
              <div className="payment-step-body">
                <div className="payment-step-title">Authorize payment</div>
                <div className="payment-step-desc">Securely authorize your card via Paymob — no charge yet.</div>
              </div>
            </li>
            <li className="payment-step">
              <span className="payment-step-num">3</span>
              <div className="payment-step-body">
                <div className="payment-step-title">Admin review</div>
                <div className="payment-step-desc">Our team reviews and approves your application.</div>
              </div>
            </li>
            <li className="payment-step">
              <span className="payment-step-num">4</span>
              <div className="payment-step-body">
                <div className="payment-step-title">Membership activated</div>
                <div className="payment-step-desc">Your card is charged and your benefits begin.</div>
              </div>
            </li>
          </ol>
        </div>

        <div className="section">
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Next Step: Payment Authorization</strong>
            <p className="mb-0 mt-2">
              After submitting this form, you'll be redirected to securely authorize your payment.
              Your card will only be charged after admin approval of your membership application.
            </p>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-right-circle me-2"></i>
                Continue to Payment
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container my-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Show the info content for the membership type the user is currently choosing.
  const activeInfoContent =
    formData.type === 2 ? pricing?.infoContentCouple : pricing?.infoContentSingle;

  return (
    <>
      <Navbar />
      <div className="membership-page">
        <div className="container">
          <div className="page-header">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h1><i className="bi bi-award me-3"></i>Membership</h1>
                <p className="lead mb-0">Join our family and enjoy exclusive benefits</p>
              </div>
              {/* Admin/Manager Navigation */}
              {user?.role && ['admin', 'manager'].includes(user.role) && (
                <div className="btn-group">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigate('/membership/payments')}
                  >
                    <i className="bi bi-credit-card me-2"></i>
                    Payments
                  </button>
                </div>
              )}
            </div>
          </div>

          {showForm && renderTypeSelector()}

          {activeInfoContent ? (
            <div
              className="membership-info membership-info-rich"
              dangerouslySetInnerHTML={{ __html: sanitizeInfoHtml(activeInfoContent) }}
            />
          ) : (
          <div className="membership-info">
            <div className="info-block">
              <h2>Membership Benefits</h2>
              <ul>
                <li>Free entrance to all the parties throughout the summer</li>
                <li>10 free bouchons to use anytime during the summer</li>
                <li>Daily access to the members only beach area</li>
                <li>Minimum-spending does not apply to members</li>
                <li>Dedicated VIP phone number for reservations</li>
                <li>Skip the line! VIP private entrance into KIKI'S</li>
                <li>Members can book the beach for non-members (Selection and day-use fees apply)</li>
                <li>Members can book parties for non-members (Selection and ticket fees apply)</li>
              </ul>
            </div>

            <div className="info-block">
              <h2>Kids Regulation</h2>
              <ul>
                <li>Kids are welcomed all week for breakfast and on beach until 6PM</li>
                <li>Members are allowed to get their kids (under 12 YO) to the members only areas for free (no need to pay for their minimum spend)</li>
                <li>Outside food or drinks are not allowed</li>
                <li>Nannies are not allowed</li>
              </ul>
            </div>

            <div className="info-block info-note">
              <h2>Additional Note</h2>
              <p>
                "Please note that the payment is done in dollars. If you face any issue,
                kindly contact us on <a href="mailto:hello@kikisexperience.com">hello@kikisexperience.com</a>"
              </p>
            </div>
          </div>
          )}

          {membershipStatus && membershipStatus.hasMembership && (
            <>
              {renderMembershipCard()}
              {renderStatusMessage()}
            </>
          )}

          {!membershipStatus?.hasMembership && renderStatusMessage()}

          {showForm && renderApplicationForm()}

          {!membershipStatus?.hasMembership && !membershipStatus?.canApply && !showForm && (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              You cannot apply for membership at this time.
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Membership;
